import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Header from "../../../component/organisms/header";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import Sidebar from "../../../component/organisms/sidebar";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import PretendardText from "../../../component/atoms/text/pretendardText";
import DatePicker from "../_components/datePicker";
import ExcelBtn from "./../../../component/atoms/button/excelBtn";
import TableText from "./../../../component/atoms/text/table/tableText";
import CancelCountChart from "./_components/cancelCountChart";
import CancelReasonChart from "./_components/cancelReasonChart";
import AnalyticsApi from "../../../api/analytics.api";
import getToday from "../../../util/getToday";
import { errorHandler } from "../../../util/errorHandler";
import { formatNumberWithCommas } from "./../../../util/formatNumber";
import moment from "moment";

const StatisticsCancel = () => {
  const today = useMemo(() => getToday(), []);

  // ---------- 탭 관련 ---------- //
  const [tab, setTab] = useState({
    main: true,
    reason: false,
  });

  useEffect(() => {
    setDateType("day");
    setIsEtcOpen(false);
    setSelectedDate("");

    if (tab.reason) {
      getCancelReasonData();
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 날짜 선택 관련 ---------- //
  // 일간, 주간, 월간
  const [dateType, setDateType] = useState("day");

  // 선택된 날짜
  const [selectedDate, setSelectedDate] = useState("");

  // 기간 타입이 변경되면 선택된 날짜 초기화
  useEffect(() => {
    setSelectedDate("");
    setCancelCountData([]);
  }, [dateType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 멤버십 해지 수 관련 ---------- //
  const [cancelCountData, setCancelCountData] = useState([]);

  const getCancelCountData = async (searchDate, startDate) => {
    const processedSearchDate = moment(searchDate).format("YYYY-MM-DD");

    let processedStartDate = "";
    if (startDate) {
      processedStartDate = moment(startDate).format("YYYY-MM-DD");
    }

    const data = {
      periodType: dateType,
      searchDate: processedSearchDate,
    };

    if (startDate) {
      data.startDate = processedStartDate;
    }

    try {
      const response = (await AnalyticsApi.getListMembership(data)).data.data
        .content;

      // response에서 daydate를 moment 돌려서 YYYY-MM-DD 형식으로 변환
      const processedResponse = response.map((item) => {
        return {
          ...item,
          date:
            dateType === "day"
              ? moment(item.daydate).format("YYYY-MM-DD")
              : dateType === "week"
              ? moment(item.weekdate).format("YYYY-MM-DD")
              : dateType === "month"
              ? moment(item.monthdate).format("YYYY-MM")
              : "",
          cancelcount: Number(item.cancelcount) || 0,
        };
      });

      setCancelCountData(processedResponse);
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (selectedDate && dateType === "day") {
      getCancelCountData(selectedDate);
    } else if (selectedDate && dateType === "week") {
      // ex) "2024-12-16 (월) → 2024-12-22 (일)"
      const [startPart, endPart] = selectedDate
        .split("→")
        .map((part) => part.trim());

      const startDate = startPart.split(" ")[0]; // ex) "2024-12-16"
      const endDate = endPart.split(" ")[0]; // ex) "2024-12-22"

      getCancelCountData(endDate, startDate);
    } else if (selectedDate && dateType === "month") {
      // selectedDate가 "2024-12" 이렇게 오면 해당하는 달의 1일을 추출
      const firstDayOfMonth = moment(selectedDate).startOf("month").format();

      getCancelCountData(selectedDate, firstDayOfMonth);
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 멤버십 해지 사유 관련 ---------- //
  // 기타 항목 펼치기
  const [isEtcOpen, setIsEtcOpen] = useState(false);

  // 데이터 가져오기
  const [cancelReasonData, setCancelReasonData] = useState([]);
  const cancelRequestReasonTitleList = [
    {
      title: "결제 방식이나 멤버십 서비스를 변경하고 싶어요",
      value: "CHANGE_MEMBERSHIP_OR_PAYMENT",
    },
    {
      title: "기대했던 것과 콘텐츠의 내용이 달라요",
      value: "CONTENT_MISMATCH",
    },
    {
      title: "가격이 부담돼요",
      value: "PRICE_BURDEN",
    },
    {
      title: "이용을 잘 안해요",
      value: "LOW_USAGE",
    },
    {
      title: "구매자를 위한 혜택이 부족해요",
      value: "LACK_OF_BENEFITS",
    },
    {
      title: "기타",
      value: "ETC",
    },
  ];
  const totalCancelReasonCount = useMemo(() => {
    return cancelReasonData
      .map((item) => item.cancelcount)
      .reduce((acc, cur) => acc + cur, 0);
  }, [cancelReasonData]);

  const getCancelReasonData = async () => {
    try {
      const data = {
        searchDate: today,
        searchType: "cancel",
      };

      const response = (await AnalyticsApi.getListMembership(data)).data.data
        .content;

      const filteredResponse = response.filter((item) => {
        return (
          item.cancelrequestreason === "CHANGE_MEMBERSHIP_OR_PAYMENT" ||
          item.cancelrequestreason === "CONTENT_MISMATCH" ||
          item.cancelrequestreason === "PRICE_BURDEN" ||
          item.cancelrequestreason === "LOW_USAGE" ||
          item.cancelrequestreason === "LACK_OF_BENEFITS" ||
          item.cancelrequestreason === "ETC"
        );
      });

      const processedResponse = filteredResponse.map((item) => {
        return {
          ...item,
          cancelcount: Number(item.cancelcount),
        };
      });

      setCancelReasonData(processedResponse);
    } catch (error) {
      errorHandler(error);
    }
  };

  // 기타 사유 가져오기
  const [etcReasonDate, setEtcReasonDate] = useState([]);

  const getEtcReasonData = async () => {
    try {
      const etcReasonResponse = (await AnalyticsApi.getListEtcReason()).data
        .data.content;

      const filteredEtcReasonResponse = etcReasonResponse.map((item) => {
        return item.cancelRequestReasonDetail;
      });

      setEtcReasonDate(filteredEtcReasonResponse);
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle>멤버십 해지 분석</TableTitle>
          <TabWrapper>
            <Tab
              isActive={tab.main}
              onClick={() => {
                setTab({
                  main: true,
                  reason: false,
                });
              }}
            >
              <TabText isActive={tab.main}>멤버십 해지 수</TabText>
            </Tab>
            <Tab
              isActive={tab.reason}
              onClick={() => {
                setTab({
                  main: false,
                  reason: true,
                });
              }}
            >
              <TabText isActive={tab.reason}>멤버십 해지 사유</TabText>
            </Tab>
          </TabWrapper>

          <TabContentContainer>
            {tab.main && (
              <>
                <TabContentHeader>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                    }}
                  >
                    <DateTypeBtnWrapper>
                      <DateTypeBtn
                        isActive={dateType === "day"}
                        onClick={() => {
                          setDateType("day");
                        }}
                      >
                        일간
                      </DateTypeBtn>
                      <DateTypeBtn
                        isActive={dateType === "week"}
                        onClick={() => {
                          setDateType("week");
                        }}
                      >
                        주간
                      </DateTypeBtn>
                      <DateTypeBtn
                        isActive={dateType === "month"}
                        onClick={() => {
                          setDateType("month");
                        }}
                      >
                        월간
                      </DateTypeBtn>
                    </DateTypeBtnWrapper>
                    <DatePicker
                      dateType={dateType === "day" ? "date" : dateType}
                      isOnlyOneDate={true}
                      onClick={() => {}}
                      date={selectedDate}
                      dateHandler={setSelectedDate}
                    />
                  </div>
                  {/* <ExcelBtn /> */}
                </TabContentHeader>

                {/* 멤버십 해지 수 - 차트 데이터 */}
                <ChartContainer>
                  <ChartHeader>
                    <PretendardText
                      style={{
                        fontWeight: 700,
                        fontSize: "16px",
                        lineHeight: "28px",
                        letterSpacing: "-0.3%",
                        color: "#f3f3f3",
                      }}
                    >
                      차트 데이터
                    </PretendardText>
                  </ChartHeader>
                  <ChartGraphWrapper>
                    <div
                      style={{
                        width: "fit-content",
                        height: 23,
                        display: "flex",
                        alignItems: "center",
                        gap: 20,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            backgroundColor: "#cf6679",
                            borderRadius: "50%",
                          }}
                        />
                        <PretendardText
                          style={{
                            color: "#aaa",
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "22.75px",
                          }}
                        >
                          멤버십 해지수
                        </PretendardText>
                      </div>
                    </div>
                  </ChartGraphWrapper>

                  <CancelCountChart
                    dataList={cancelCountData}
                    dateType={dateType}
                  />

                  {/* 테이블 영역 */}
                  <div
                    style={{
                      width: "100%",
                      height: "fit-content",
                      display: "flex",
                      justifyContent: "center",
                      gap: 30,
                      marginBottom: 40,
                    }}
                  >
                    <table
                      style={{
                        width: "435px",
                        borderSpacing: 0,
                        borderCollapse: "collapse",
                      }}
                    >
                      <colgroup>
                        {/* 합계: 435px */}
                        {/* 날짜 */}
                        <col width="322px" />
                        {/* 멤버십 해지 수 */}
                        <col width="113px" />
                      </colgroup>
                      <thead>
                        <tr
                          style={{
                            height: 41,
                            backgroundColor: "#313233",
                            borderTop: "0.5px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <th>
                            <TableText
                              style={{
                                fontWeight: "600",
                                lineHeight: "22.75px",
                                paddingLeft: 20,
                              }}
                            >
                              날짜
                            </TableText>
                          </th>
                          <th>
                            <TableText
                              style={{
                                fontWeight: "600",
                                lineHeight: "22.75px",
                                textAlign: "right",
                                marginRight: 33,
                              }}
                            >
                              멤버십 해지 수
                            </TableText>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cancelCountData.map((item, index) => {
                          // 0 ~ 4까지만 보여주기
                          if (index > 4) return null;
                          return (
                            <>
                              <tr
                                key={index}
                                style={{
                                  height: 40,
                                  borderBottom:
                                    "0.5px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <td>
                                  <TableText
                                    style={{
                                      paddingLeft: 20,
                                    }}
                                  >
                                    {dateType === "day" &&
                                      moment(item.daydate).format(
                                        "YYYY-MM-DD (ddd)"
                                      )}

                                    {dateType === "week" && (
                                      <>
                                        {moment(item.weekdate).format(
                                          "YYYY-MM-DD (ddd)"
                                        )}
                                        {" → "}
                                        {moment(item.weekdate)
                                          .add(6, "days")
                                          .format("YYYY-MM-DD (ddd)")}
                                      </>
                                    )}

                                    {dateType === "month" && (
                                      <>
                                        {moment(item.monthdate).format(
                                          "YYYY-MM"
                                        )}{" "}
                                        월간
                                      </>
                                    )}
                                  </TableText>
                                </td>
                                <td>
                                  <TableText
                                    style={{
                                      textAlign: "right",
                                      marginRight: 33,
                                    }}
                                  >
                                    {formatNumberWithCommas(item.cancelcount)}
                                  </TableText>
                                </td>
                              </tr>
                            </>
                          );
                        })}
                      </tbody>
                    </table>

                    <table
                      style={{
                        width: "435px",
                        borderSpacing: 0,
                        borderCollapse: "collapse",
                      }}
                    >
                      <colgroup>
                        {/* 합계: 435px */}
                        {/* 날짜 */}
                        <col width="322px" />
                        {/* 멤버십 해지 수 */}
                        <col width="113px" />
                      </colgroup>
                      <thead>
                        <tr
                          style={{
                            height: 41,
                            backgroundColor: "#313233",
                            borderTop: "0.5px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <th>
                            <TableText
                              style={{
                                fontWeight: "600",
                                lineHeight: "22.75px",
                                paddingLeft: 20,
                              }}
                            >
                              날짜
                            </TableText>
                          </th>
                          <th>
                            <TableText
                              style={{
                                fontWeight: "600",
                                lineHeight: "22.75px",
                                textAlign: "right",
                                marginRight: 33,
                              }}
                            >
                              멤버십 해지 수
                            </TableText>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cancelCountData.map((item, index) => {
                          // 5 ~ 9까지만 보여주기
                          if (index < 5) return null;

                          return (
                            <>
                              <tr
                                key={index}
                                style={{
                                  height: 40,
                                  borderBottom:
                                    "0.5px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <td>
                                  <TableText
                                    style={{
                                      paddingLeft: 20,
                                    }}
                                  >
                                    {dateType === "day" &&
                                      moment(item.daydate).format(
                                        "YYYY-MM-DD (ddd)"
                                      )}

                                    {dateType === "week" && (
                                      <>
                                        {moment(item.weekdate).format(
                                          "YYYY-MM-DD (ddd)"
                                        )}
                                        {" → "}
                                        {moment(item.weekdate)
                                          .add(6, "days")
                                          .format("YYYY-MM-DD (ddd)")}
                                      </>
                                    )}

                                    {dateType === "month" && (
                                      <>
                                        {moment(item.monthdate).format(
                                          "YYYY-MM"
                                        )}{" "}
                                        월간
                                      </>
                                    )}
                                  </TableText>
                                </td>
                                <td>
                                  <TableText
                                    style={{
                                      textAlign: "right",
                                      marginRight: 33,
                                    }}
                                  >
                                    {formatNumberWithCommas(item.cancelcount)}
                                  </TableText>
                                </td>
                              </tr>
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </ChartContainer>
              </>
            )}

            {/* ********** 멤버십 해지 사유 ********** */}
            {tab.reason && (
              <>
                {/* <TabContentHeader>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <ExcelBtn />
                  </div>
                </TabContentHeader> */}

                {/* 멤버십 해지 사유 - 차트 데이터 */}
                <ChartContainer>
                  <ChartHeader>
                    <PretendardText
                      style={{
                        fontWeight: 700,
                        fontSize: "16px",
                        lineHeight: "28px",
                        letterSpacing: "-0.3%",
                        color: "#f3f3f3",
                      }}
                    >
                      차트 데이터
                    </PretendardText>
                  </ChartHeader>
                  <ChartGraphWrapper>
                    <PretendardText
                      style={{
                        color: "rgba(181, 181, 181, 0.5)",
                        fontSize: "14px",
                        lineHeight: "22px",
                        letterSpacing: "-0.3%",
                        fontWeight: 600,
                      }}
                    >
                      기간 : ~현재({today})
                    </PretendardText>
                  </ChartGraphWrapper>

                  <CancelReasonChart dataList={cancelReasonData} />

                  {/* 테이블 영역 */}
                  <table
                    style={{
                      width: "924px",
                      borderSpacing: 0,
                      borderCollapse: "collapse",
                      marginBottom: isEtcOpen ? 16 : 140,
                      position: "relative",
                    }}
                  >
                    <colgroup>
                      {/* 합계: 924px */}
                      {/* 항목 */}
                      <col width="704px" />
                      {/* 응답수 */}
                      <col width="220px" />
                    </colgroup>
                    <thead>
                      <tr
                        style={{
                          height: 41,
                          backgroundColor: "#313233",
                          borderTop: "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              paddingLeft: 32,
                            }}
                          >
                            항목
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                              marginRight: 129,
                            }}
                          >
                            응답수
                          </TableText>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelReasonData.map((item, index) => {
                        if (item.cancelrequestreason === "ETC") return null;

                        return (
                          <>
                            <tr
                              key={index}
                              style={{
                                height: 40,
                                borderBottom:
                                  "0.5px solid rgba(255, 255, 255, 0.12)",
                              }}
                            >
                              <td>
                                <TableText
                                  style={{
                                    paddingLeft: 32,
                                  }}
                                >
                                  {
                                    cancelRequestReasonTitleList.find(
                                      (title) =>
                                        title.value === item.cancelrequestreason
                                    ).title
                                  }
                                </TableText>
                              </td>
                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 5,
                                    marginRight: 129,
                                  }}
                                >
                                  <TableText>
                                    {formatNumberWithCommas(item.cancelcount)}명
                                  </TableText>
                                  <TableText
                                    style={{
                                      color: "#009bde",
                                    }}
                                  >
                                    (
                                    {(
                                      (item.cancelcount /
                                        totalCancelReasonCount) *
                                      100
                                    ).toFixed(1)}
                                    %)
                                  </TableText>
                                </div>
                              </td>
                            </tr>
                          </>
                        );
                      })}

                      <tr
                        style={{
                          height: 40,
                          borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <TableText
                              style={{
                                paddingLeft: 32,
                              }}
                            >
                              기타
                            </TableText>
                            <img
                              className={isEtcOpen ? "rotate" : "rotate-back"}
                              src="/assets/admin/icons/ico_select_arrow_down.svg"
                              alt="펼치기"
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                if (etcReasonDate.length === 0) {
                                  getEtcReasonData();
                                }

                                setIsEtcOpen(!isEtcOpen);
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 5,
                              marginRight: 129,
                            }}
                          >
                            <TableText>
                              {cancelReasonData.find(
                                (item) => item.cancelrequestreason === "ETC"
                              ) === undefined
                                ? 0
                                : cancelReasonData.find(
                                    (item) => item.cancelrequestreason === "ETC"
                                  ).cancelcount}
                              명
                            </TableText>
                            <TableText
                              style={{
                                color: "#009bde",
                              }}
                            >
                              (
                              {(cancelReasonData.find(
                                (item) => item.cancelrequestreason === "ETC"
                              ) === undefined
                                ? 0
                                : cancelReasonData.find(
                                    (item) => item.cancelrequestreason === "ETC"
                                  ).cancelcount / totalCancelReasonCount
                              ).toFixed(1)}
                              %)
                            </TableText>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {isEtcOpen && (
                    <ul
                      style={{
                        width: "924px",
                        marginBottom: 120,
                        listStyle: "disc",
                        listStylePosition: "inside",
                        listColor: "#aaa",
                      }}
                    >
                      {etcReasonDate.map((item, index) => {
                        return (
                          <li
                            key={index}
                            style={{
                              fontFamily: "Pretendard",
                              fontSize: "13px",
                              fontWeight: "500",
                              lineHeight: "20px",
                              color: "#aaa",
                            }}
                          >
                            {item}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </ChartContainer>
              </>
            )}
          </TabContentContainer>
        </DetailPageLayout>
      </MainLayout>
    </>
  );
};

export default StatisticsCancel;

const TabWrapper = styled.div.attrs((props) => {})`
  width: fit-content;
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 32px;
`;

const Tab = styled.div.attrs((props) => {})`
  width: fit-content;
  height: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 19px 34.5px;
  border-bottom: ${(props) => (props.isActive ? "2px solid #27beff" : "none")};
  cursor: pointer;
`;

const TabText = styled(PretendardText).attrs((props) => {})`
  font-weight: 700;
  font-size: 16px;
  line-height: 25px;
  letter-spacing: -0.3%;
  color: ${(props) =>
    props.isActive ? "#27beff" : "rgba(181, 181, 181, 0.5)"};
`;

const TabContentContainer = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`;

const TabContentHeader = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const DateTypeBtnWrapper = styled.div.attrs((props) => {})`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DateTypeBtn = styled(PretendardText).attrs((props) => {})`
  width: fit-content;
  height: fit-content;
  padding: 3px 10px;
  border-radius: 4px;
  border: ${(props) =>
    props.isActive
      ? "1px solid rgba(0, 155, 222, 1)"
      : "1px solid rgba(255, 255, 255, 0.12)"};
  background-color: #272829;
  font-weight: 500;
  font-size: 14px;
  line-height: 22.75px;
  text-align: center;
  color: ${(props) => (props.isActive ? "#009bde" : "#aaa")};
  cursor: pointer;
`;

const ChartContainer = styled.div.attrs((props) => {})`
  width: 1044px;
  height: fit-content;
  min-height: 439px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #272829;
  box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.08);
`;

const ChartHeader = styled.div.attrs((props) => {})`
  width: 100%;
  height: 59px;
  display: flex;
  align-items: center;
  padding: 0px 30px;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.12);
`;

const ChartGraphWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  padding: 24px 30px;
`;
